import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FavoritesNav } from '../favorites-nav/favorites-nav';
import ParticipantMatchList from '../match-list/containers/participant-match-list/participant-match-list';
import { RpcStateStatus, useRpcState, WidgetsRouteChangeDetails, WidgetsRouteName } from '@om-fe/widgets';
import { DisciplineLiveStatus, ParticipantModel, TournamentModel } from '@om-fe/models';
import { MatchListItemLayout } from '../match-list/utils/match-list.constants';
import { DEFAULT_LIMIT_NUMBER_OF_EVENTS } from '../../common/constants';
import FavoriteMixedMatchList from '../match-list/containers/favorite-match-list-v2/favorite-mixed-match-list';
import { FAVORITE_TAB } from './favorites-content-tab';
import { PlatformTypes } from '@om-fe/utils';

export interface GetMarketGroupsResponse {
    readonly format: string;
    readonly records: ParticipantModel[] | TournamentModel[];
    readonly version: string;
}

export interface FavoriteContentProps {
    readonly favoriteTeamsList: string[];
    readonly favoriteTournamentList?: string[];
    readonly favoriteEvent?: number;
    readonly mainMarketsLimit?: number;
    readonly lang: string;
    readonly activeTab: string;
    readonly activeType: string;
    readonly activeItem: string;
    readonly deviceType?: PlatformTypes;
}
export interface FavoritesNavState {
    readonly type?: string;
    readonly item?: (ParticipantModel | TournamentModel)[];
}

export interface FavoritesNavItemsState {
    readonly items?: ParticipantModel | TournamentModel;
}
function FavoriteContent (
    {
        favoriteTeamsList,
        mainMarketsLimit,
        favoriteEvent,
        lang,
        activeTab: activatedTab,
        activeType,
        activeItem: activatedItem,
        deviceType,
    }: FavoriteContentProps,
) {
    const [item, setItem] = useState<(FavoritesNavItemsState | ParticipantModel | TournamentModel)[]>([]);
    const [activeTab, setActiveTab] = useState<string>(activatedTab);
    const [activeTypeTab, setActiveTypeTab] = useState<string>(activeType);
    const [activeItem, setActiveItem] = useState<string>(activatedItem);
    const teamsParams = useMemo(() => {
        return { participantIds: favoriteTeamsList ?? [], lang };
    }, [favoriteTeamsList, lang]);
    const [rpcTeamsResponse] = useRpcState<GetMarketGroupsResponse>('participants', teamsParams);

    useEffect(()=>{
        if (rpcTeamsResponse.status === RpcStateStatus.Success) {
            const { data: teamsData } = rpcTeamsResponse;
            const { records: recordsTeams } = teamsData;
            let combineData: (FavoritesNavItemsState | ParticipantModel | TournamentModel)[] = [];

            recordsTeams.map((item: ParticipantModel | TournamentModel) => {
                combineData.push(item);

                return item;
            });

            setItem(combineData);

            if (combineData[activeTab]?._type && combineData[activeTab]?._type !== activeTypeTab) {
                setActiveItem(combineData[activeTab].id);
                setActiveTypeTab(combineData[activeTab]._type);
            } else if (combineData[activeTab]?.id && combineData[activeTab]?.id !== activeItem) {
                setActiveItem(combineData[activeTab].id);
            } else {
                if (typeof combineData[activeTab] === 'undefined' && activeTab !== FAVORITE_TAB.EVENT) {
                    const tab = (parseInt(activeTab) - 1).toString();

                    setActiveTab(tab)
                    setActiveItem(combineData[tab].id);
                }
            }
        }
    },[rpcTeamsResponse, favoriteEvent, activeTab, activeTypeTab, activeItem]);

    const handleRouteChange = useCallback((details: WidgetsRouteChangeDetails<WidgetsRouteName.Sport>) => {
        if (details.params.participantId) {
            const activeTeams =  Object.keys(item).filter(key => item[key].id === details.params.participantId);
            setActiveTab(activeTeams[0]);
            setActiveItem(details.params.participantId);
            setActiveTypeTab(FAVORITE_TAB.PARTICIPANT);
        } else {
            setActiveItem('');
            setActiveTab(FAVORITE_TAB.EVENT);
            setActiveTypeTab(FAVORITE_TAB.EVENT);
        }
    }, [item]);

    if (
        rpcTeamsResponse.status === RpcStateStatus.Loading ||
        rpcTeamsResponse.status === RpcStateStatus.Initial ||
        rpcTeamsResponse.status === RpcStateStatus.Failed
    ) {

        return null;
    }

    const content = () => {

        if (activeTypeTab === FAVORITE_TAB.PARTICIPANT) {
            return (
                <ParticipantMatchList
                    participantId={activeItem}
                    instanceId={`favorite-page-match-list`}
                    liveStatus={DisciplineLiveStatus.BOTH}
                    limitNumberOfEvents={DEFAULT_LIMIT_NUMBER_OF_EVENTS}
                    marketsHeaderOptions={'show_tournament'}
                    mainMarketsLimit={mainMarketsLimit ?? 3}
                    showMatchListHeader={false}
                    showMarketInMatchListGroupHeader={true}
                    itemLayout={MatchListItemLayout.V2}
                />
            );
        }

        return (
            <FavoriteMixedMatchList
                instanceId={`LiveEventFavorites`}
                liveStatus={DisciplineLiveStatus.BOTH}
                limitNumberOfEvents={20}
                mainMarketsLimit={mainMarketsLimit || 3}
            />
        );
    }

    return (
        <>
            <FavoritesNav 
                list={item}
                activeTab={activeTab}
                favoriteEvent={favoriteEvent}
                hasArrows={deviceType === 'desktop'}
                onRouteChange={handleRouteChange}
            />
            {content()}
        </>
    );
};

export default FavoriteContent;
